/**
 * PanelPagination — the ONE pager for WorkspacePanel lists (DD-015).
 * Panels grow with their content instead of scrolling internally; long
 * lists are bounded by paging, not by a fixed panel height. Renders
 * nothing when everything fits on one page.
 */

import { useMemo, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@/components/icons";

export function usePagedList<T>(items: T[], pageSize: number) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  // Clamp instead of resetting so filter changes never leave a dead page.
  const safePage = Math.min(page, pageCount - 1);
  const paged = useMemo(
    () => items.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [items, safePage, pageSize]
  );
  return {
    paged,
    page: safePage,
    pageCount,
    total: items.length,
    start: items.length === 0 ? 0 : safePage * pageSize + 1,
    end: Math.min(items.length, (safePage + 1) * pageSize),
    prev: () => setPage(Math.max(0, safePage - 1)),
    next: () => setPage(Math.min(pageCount - 1, safePage + 1)),
  };
}

interface PanelPaginationProps {
  page: number;
  pageCount: number;
  total: number;
  start: number;
  end: number;
  onPrev: () => void;
  onNext: () => void;
}

export const PanelPagination: React.FC<PanelPaginationProps> = ({
  page,
  pageCount,
  total,
  start,
  end,
  onPrev,
  onNext,
}) => {
  if (pageCount <= 1) return null;
  return (
    // mt-auto pins the pager to the bottom when the panel body is a
    // stretched flex column; bottom-right per DD-015.
    <div className="mt-auto flex items-center justify-end gap-2 pt-3">
      <span className="text-xs text-gray-400">
        {start}–{end} of {total}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onPrev}
          disabled={page === 0}
          aria-label="Previous page"
          className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={page >= pageCount - 1}
          aria-label="Next page"
          className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
